import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loading } from "@/components/States";
import { api, fetchSubjects, formatError } from "@/lib/api";

const EMPTY = { name: "", short: "", description: "", icon: "BookOpen", active: true, topics: [] };

function SubjectEditor({ subject, onSaved }) {
  const [f, setF] = useState(subject || EMPTY);
  const isNew = !subject;
  const setTopic = (i, k, v) => setF({ ...f, topics: f.topics.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const save = async () => {
    try {
      const body = { ...f, topics: f.topics.filter((t) => t.name.trim()).map((t) => ({ ...t, subtopics: (t.subtopics || []).map((x) => x.trim()).filter(Boolean) })) };
      if (isNew) await api.post("/admin/subjects", body); else await api.put(`/admin/subjects/${subject.id}`, body);
      toast.success("Subject saved"); fetchSubjects(true); onSaved();
      if (isNew) setF(EMPTY);
    } catch (e) { toast.error(formatError(e)); }
  };
  const tid = subject?.id || "new";
  return (
    <div data-testid={`admin-subject-${tid}`} className="rounded-xl border border-line bg-surface p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Input data-testid={`subject-name-${tid}`} placeholder="Subject name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid={`subject-short-${tid}`} placeholder="Short code (e.g. CDM)" value={f.short} disabled={!isNew} onChange={(e) => setF({ ...f, short: e.target.value })} />
        <label className="flex items-center gap-2 text-sm"><Switch data-testid={`subject-active-${tid}`} checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />Active</label>
      </div>
      <Textarea className="mt-3" placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wider text-ink-3">Topics, reference notes & subtopics (used to ground AI)</p>
      <div className="flex flex-col gap-2">
        {f.topics.map((t, i) => (
          <div key={t.id || i} className="grid gap-2 sm:grid-cols-[1fr_1.4fr_1.4fr_auto]">
            <Input value={t.name} placeholder="Topic" onChange={(e) => setTopic(i, "name", e.target.value)} />
            <Input value={t.notes || ""} placeholder="Reference notes (optional)" onChange={(e) => setTopic(i, "notes", e.target.value)} />
            <Input data-testid={`subject-subtopics-${tid}-${i}`} value={(t.subtopics || []).join(";")} placeholder="Subtopics, separated by ;" onChange={(e) => setTopic(i, "subtopics", e.target.value.split(";"))} />
            <Button variant="ghost" size="icon" onClick={() => setF({ ...f, topics: f.topics.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button data-testid={`subject-add-topic-${tid}`} variant="outline" size="sm" onClick={() => setF({ ...f, topics: [...f.topics, { name: "", notes: "", subtopics: [] }] })}><Plus className="mr-1 h-4 w-4" />Add topic</Button>
        <Button data-testid={`subject-save-${tid}`} size="sm" onClick={save}>{isNew ? "Create subject" : "Save"}</Button>
      </div>
    </div>
  );
}

export default function AdminSubjects() {
  const [subjects, setSubjects] = useState(null);
  const load = () => api.get("/admin/subjects").then((r) => setSubjects(r.data));
  useEffect(() => { load(); }, []);
  if (!subjects) return <Loading />;
  return (
    <div className="flex flex-col gap-4">
      {subjects.map((s) => <SubjectEditor key={s.id} subject={s} onSaved={load} />)}
      <h3 className="mt-4 font-bold text-ink">Add a new subject</h3>
      <SubjectEditor onSaved={load} />
    </div>
  );
}
